import type { CopilotRecommendation, RecommendationPriority } from "../hooks/useCopilot"
import type { VisitCardUpdateFields } from "../hooks/useVisitCard"
import type { CopilotSummary } from "../types"
import type { RecommendationItem } from "../copilotEvents"
import copilotDemo from "./copilot_demo.json"
import copilotDemoSummary from "./copilot_demo_summary.json"
import checkpointsDemo from "./checkpoints_demo.json"
import visitCardUpdates from "./visit_card_updates.json"

export type DemoCopilotRec = {
  rec_id: string
  chunk_index: number
  t_sec: number
  color: string
  category: string
  reason_category: string
  text: string
}

export type DemoCheckpoint = {
  key: string
  t_sec: number
}

export type DemoVisitCardUpdate = {
  t_sec: number
  fields: VisitCardUpdateFields
}

export const DEMO_DIALOG_AUDIO_PATH = "/demo/dialog_demo.mp3"
export const DEMO_START_RECORDING_STEP_ID = "start-recording"
export const DEMO_COPILOT_FIRST_RECOMMENDATION_STEP_ID = "copilot-first-recommendation"
/** @deprecated Use DEMO_COPILOT_FIRST_RECOMMENDATION_STEP_ID */
export const DEMO_COPILOT_PANEL_STEP_ID = DEMO_COPILOT_FIRST_RECOMMENDATION_STEP_ID
export const DEMO_SUMMARY_DELAY_MS = 1500

export const DEMO_COPILOT_RECS = copilotDemo as DemoCopilotRec[]
export const DEMO_COPILOT_SUMMARY = copilotDemoSummary as CopilotSummary
export const DEMO_CHECKPOINTS = checkpointsDemo as DemoCheckpoint[]
export const DEMO_VISIT_CARD_UPDATES =
  visitCardUpdates as unknown as DemoVisitCardUpdate[]

/** Map demo JSON color labels to CopilotPanel priority borders. */
export function colorToPriority(color: string): RecommendationPriority {
  const normalized = color.trim().toLowerCase()
  if (normalized === "красный" || normalized === "красная" || normalized === "красное") {
    return "high"
  }
  if (normalized === "зелёный" || normalized === "зеленый" || normalized === "зелёная" || normalized === "зеленая") {
    return "low"
  }
  // жёлтый / оранжевый / anything else → medium
  return "medium"
}

export function demoRecToCopilotRecommendation(rec: DemoCopilotRec): CopilotRecommendation {
  const item: RecommendationItem = {
    priority: colorToPriority(rec.color),
    type: rec.category,
    text: rec.text,
    rationale: rec.category,
    suggested_phrase: "",
    confidence: 1,
  }
  return {
    ...item,
    id: rec.rec_id,
    chunkIndex: rec.chunk_index,
    elapsedSec: rec.t_sec,
    receivedAt: Date.now(),
  }
}

/**
 * Given audio currentTime (sec) and which rec indices were already emitted,
 * return newly due recommendations (in JSON order) whose t_sec <= currentTime.
 */
export function dueDemoRecommendations(
  currentTimeSec: number,
  alreadyEmitted: ReadonlySet<number>,
  recs: readonly DemoCopilotRec[] = DEMO_COPILOT_RECS,
): CopilotRecommendation[] {
  const due: CopilotRecommendation[] = []
  for (let i = 0; i < recs.length; i += 1) {
    if (alreadyEmitted.has(i)) continue
    if (recs[i].t_sec > currentTimeSec) continue
    due.push(demoRecToCopilotRecommendation(recs[i]))
  }
  return due
}

/**
 * Given audio currentTime (sec) and which checkpoint indices were already emitted,
 * return newly due checkpoint keys (in JSON order) whose t_sec <= currentTime.
 */
export function dueDemoCheckpoints(
  currentTimeSec: number,
  alreadyEmitted: ReadonlySet<number>,
  checkpoints: readonly DemoCheckpoint[] = DEMO_CHECKPOINTS,
): string[] {
  const due: string[] = []
  for (let i = 0; i < checkpoints.length; i += 1) {
    if (alreadyEmitted.has(i)) continue
    if (checkpoints[i].t_sec > currentTimeSec) continue
    due.push(checkpoints[i].key)
  }
  return due
}

/**
 * Given audio currentTime (sec) and which visit-card update indices were already
 * applied, return newly due field payloads (in JSON order).
 */
export function dueDemoVisitCardUpdates(
  currentTimeSec: number,
  alreadyEmitted: ReadonlySet<number>,
  updates: readonly DemoVisitCardUpdate[] = DEMO_VISIT_CARD_UPDATES,
): DemoVisitCardUpdate[] {
  const due: DemoVisitCardUpdate[] = []
  for (let i = 0; i < updates.length; i += 1) {
    if (alreadyEmitted.has(i)) continue
    if (updates[i].t_sec > currentTimeSec) continue
    due.push(updates[i])
  }
  return due
}
